import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDesign,
  getStatus,
  listRules,
  lookupDecision,
  publishConfig,
  toastPublishResult,
  updateDesign,
  updateRule,
  type SafegeoRule,
} from "../lib/safegeo-api";
import { countryLabel, isFreePlan } from "../lib/format";
import { fieldChecked, fieldValue } from "../lib/polaris";
import { queryKeys } from "../lib/query-keys";
import { UpgradeNowButton } from "../components/UpgradeNowButton";
import { BlockPreview } from "../components/BlockPreview";
import { AppButton } from "../components/AppButton";
import { AppLink } from "../components/AppLink";
import { SafePage } from "../components/SafePage";
import { PhaseBadge, phaseFromStatus } from "../components/PhaseBadge";
import "../styles/preview-stage.css";
import { SaveBarHost } from "../components/SaveBarHost";
import { PreviewAsVisitor } from "../components/PreviewAsVisitor";
import { ErrorBanner } from "../components/PageBanner";
import { showToast } from "../lib/feedback";
import { snapshotEqual, useSaveBar } from "../lib/save-bar";

const COPY_SAVE_ID = "safegeo-block-copy";

type BlockCopyDraft = {
  title: string;
  message: string;
  showContact: boolean;
};

type Props = {
  /** When true, render panel only (Redirects tab) - no SafePage chrome. */
  embedded?: boolean;
};

export function BlockPage({ embedded = false }: Props) {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [copyDraft, setCopyDraft] = useState<BlockCopyDraft | null>(null);
  const [copySaved, setCopySaved] = useState<BlockCopyDraft | null>(null);

  const statusQ = useQuery({ queryKey: queryKeys.status, queryFn: getStatus });
  const rulesQ = useQuery({ queryKey: queryKeys.rules, queryFn: listRules });
  const designQ = useQuery({
    queryKey: queryKeys.design,
    queryFn: getDesign,
    enabled: !isFreePlan(statusQ.data?.plan, statusQ.data?.plan_key),
  });

  const free = isFreePlan(statusQ.data?.plan, statusQ.data?.plan_key);
  const blockRules = (rulesQ.data?.items ?? []).filter(
    (r) => !r.is_fallback && r.action === "block",
  );
  const rulesMax = statusQ.data?.entitlements?.rules_max ?? 0;
  const customCount = (rulesQ.data?.items ?? []).filter(
    (r) => !r.is_fallback,
  ).length;
  const atRulesMax = customCount >= rulesMax && rulesMax > 0;

  useEffect(() => {
    const d = designQ.data;
    if (!d) return;
    const next: BlockCopyDraft = {
      title: d.block_title || "Sorry, we don’t ship to {country}",
      message:
        d.block_message || "This store isn’t available in your country.",
      showContact: d.block_show_contact !== false,
    };
    setCopyDraft(next);
    setCopySaved(next);
  }, [designQ.data]);

  const copy =
    copyDraft ||
    ({
      title: "Sorry, we don’t ship to {country}",
      message: "This store isn’t available in your country.",
      showContact: true,
    } satisfies BlockCopyDraft);
  const copyDirty =
    !!copySaved && !!copyDraft && !snapshotEqual(copyDraft, copySaved);
  useSaveBar(COPY_SAVE_ID, copyDirty && !free);

  useEffect(() => {
    if (params.get("saved") !== "1") return;
    if (params.get("publish_warning")) {
      setPageError(
        "Saved, but storefront publish is still pending. Try Publish again.",
      );
      return;
    }
    showToast("Saved and live");
  }, [params]);

  const afterMutate = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => {
      await fn();
      try {
        await publishConfig();
      } catch {
        /* ok */
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["safegeo"] }),
  });

  async function toggle(id: string, enabled: boolean) {
    setBusy(true);
    try {
      await afterMutate.mutateAsync(() =>
        updateRule(id, { is_enabled: enabled }),
      );
    } finally {
      setBusy(false);
    }
  }

  async function saveCopy() {
    if (!copyDirty || !copyDraft) return;
    setBusy(true);
    setPageError(null);
    try {
      await updateDesign({
        block_title: copyDraft.title.slice(0, 120),
        block_message: copyDraft.message.slice(0, 280),
        block_show_contact: copyDraft.showContact,
      });
      let published = null;
      try {
        published = await publishConfig();
      } catch {
        /* design saved */
      }
      const pub = toastPublishResult(published, "Block page saved and live");
      if (!pub.ok && pub.message) setPageError(pub.message);
      setCopySaved(copyDraft);
      void qc.invalidateQueries({ queryKey: queryKeys.design });
    } catch (e) {
      setPageError(e instanceof Error ? e.message : "Couldn’t save copy");
    } finally {
      setBusy(false);
    }
  }

  if (free) {
    const freeBody = (
      <s-stack gap="base">
        <s-banner tone="info" heading="Block country is on Starter">
          You can still see how it works below.{" "}
          <UpgradeNowButton plan="starter" variant="secondary" />
        </s-banner>
        <s-section heading="Blocked countries">
          <s-box padding="base" borderWidth="base" borderRadius="base">
            <s-stack gap="base">
              <s-text type="strong">Brazil</s-text>
              <s-paragraph color="subdued">
                Visitors see your Block page · example
              </s-paragraph>
              <s-badge>Starter+</s-badge>
            </s-stack>
          </s-box>
        </s-section>
        <s-section heading="What blocked visitors see">
          <s-stack gap="base">
            <s-text-field
              label="Title"
              value="Sorry, we don’t ship to {country}"
              disabled
            />
            <s-text-field
              label="Message"
              value="This store isn’t available in your country."
              multiline={3}
              disabled
            />
            <s-paragraph color="subdued">
              Contact email comes from your shop contact. A reference code
              (SG-XXXX) is added automatically on the block page.
            </s-paragraph>
            <BlockPreview
              title="Sorry, we don’t ship to Brazil"
              message="This store isn’t available in your country."
              showContact
            />
          </s-stack>
        </s-section>
        {!embedded ? <SgLookupSection blockRules={[]} /> : null}
      </s-stack>
    );
    if (embedded) return freeBody;
    const freePhase = phaseFromStatus({
      published: !!statusQ.data?.last_published_at,
      paused: !!statusQ.data?.settings?.paused,
    });
    return (
      <SafePage
        screen="block"
        heading="Block country"
        accessory={<PhaseBadge phase={freePhase} />}
        primaryAction={undefined}
      >
        {freeBody}
      </SafePage>
    );
  }

  const previewTitle = copy.title.replace(/\{country\}/gi, "Brazil");
  const previewMessage = copy.message.replace(/\{country\}/gi, "Brazil");

  const body = (
    <>
      <SaveBarHost
        id={COPY_SAVE_ID}
        saving={busy}
        onSave={() => void saveCopy()}
        onDiscard={() => {
          if (copySaved) setCopyDraft(copySaved);
        }}
      />
      
      {pageError ? (
        <ErrorBanner heading="Couldn’t update Block">{pageError}</ErrorBanner>
      ) : null}

      {!embedded ? <SgLookupSection blockRules={blockRules} /> : null}

      <s-section
        heading="Blocked countries"
        subheading="Stop visitors from countries you don’t sell to. Search bots aren’t blocked, so Google can still index your store. EU countries need a legal confirmation when you add them."
      >
        <s-paragraph color="subdued">
          Blocking happens in the visitor’s browser. Shopify Analytics may still
          count blocked visits, and someone who turns off JavaScript can still
          reach your store.{" "}
          <AppLink to="/app/help">How blocking works ›</AppLink>
        </s-paragraph>

        {rulesQ.isLoading ? (
          <s-paragraph color="subdued">Loading…</s-paragraph>
        ) : null}
        {rulesQ.error ? (
          <ErrorBanner heading="Couldn’t load rules">
            {(rulesQ.error as Error).message}
          </ErrorBanner>
        ) : null}

        <BlockTable
          rules={blockRules}
          busy={busy}
          onToggle={(id, on) => void toggle(id, on)}
        />
      </s-section>

      <s-section heading="What blocked visitors see">
        <BlockCopyEditor
          copy={copy}
          setCopyDraft={setCopyDraft}
          previewTitle={previewTitle}
          previewMessage={previewMessage}
        />
      </s-section>

      {!embedded ? (
        <PreviewAsVisitor
          context="block"
          prefer={
            statusQ.data?.settings?.market_countries?.length
              ? statusQ.data.settings.market_countries
              : undefined
          }
        />
      ) : null}
    </>
  );

  if (embedded) return body;

  const phase = phaseFromStatus({
    published: !!statusQ.data?.last_published_at,
    paused: !!statusQ.data?.settings?.paused,
  });

  return (
    <SafePage
      screen="block"
      heading="Block country"
      accessory={<PhaseBadge phase={phase} />}
      primaryAction={
        <AppButton
          slot="primary-action"
          to={atRulesMax ? "/app/billing" : "/app/block/new"}
        >
          {atRulesMax ? "Rule limit reached" : "Add rule"}
        </AppButton>
      }
    >
      {body}
    </SafePage>
  );
}

/** v7 §15.3: Look up SG-XXXX via Nest decision refs (~30 days). */
function SgLookupSection({
  blockRules,
}: {
  blockRules: SafegeoRule[];
}) {
  const [params] = useSearchParams();
  const [code, setCode] = useState(() =>
    (params.get("code") || "").toUpperCase(),
  );
  const [looked, setLooked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hit, setHit] = useState<{
    code: string;
    country_code?: string;
    event?: string;
    is_test?: boolean;
    created_at?: string;
    message?: string;
    found: boolean;
  } | null>(null);

  useEffect(() => {
    if (params.get("lookup") === "1") {
      document.getElementById("sg-lookup-field")?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [params]);

  async function lookup() {
    const normalized = code.trim().toUpperCase().replace(/\s+/g, "");
    setLooked(true);
    setHit(null);
    if (
      !/^SG-[A-Z0-9]{4,12}$/i.test(normalized) &&
      !/^SG[A-Z0-9]{4,12}$/i.test(normalized)
    ) {
      return;
    }
    setBusy(true);
    try {
      const r = await lookupDecision(normalized);
      setHit({
        found: !!r.found,
        code: r.code || normalized,
        country_code: r.country_code,
        event: r.event,
        is_test: r.is_test,
        created_at: r.created_at,
        message: r.message,
      });
    } catch (e) {
      setHit({
        found: false,
        code: normalized,
        message: e instanceof Error ? e.message : "Couldn’t look up that code",
      });
    } finally {
      setBusy(false);
    }
  }

  const matchedRule =
    hit?.found && hit.country_code
      ? blockRules.find((r) =>
          (r.countries || []).some(
            (c) => c.toUpperCase() === hit.country_code!.toUpperCase(),
          ),
        )
      : undefined;
  const invalid =
    looked &&
    !/^SG-[A-Z0-9]{4,12}$/i.test(code.trim()) &&
    !/^SG[A-Z0-9]{4,12}$/i.test(code.trim());

  const eventLabel =
    hit?.event === "block"
      ? "blocked"
      : hit?.event === "suggest" || hit?.event === "suggest_go"
        ? "asked"
        : hit?.event === "redirect" || hit?.event === "market"
          ? "redirected"
          : hit?.event || "decision";

  return (
    <s-section
      heading="Someone says they’re blocked?"
      subheading="Look up the code on their screen (like SG-XXXX). Codes are kept about 30 days."
    >
      <s-stack direction="inline" gap="base" alignItems="end">
        <s-box minInlineSize="200px">
          <s-text-field
            id="sg-lookup-field"
            label="Reference code"
            value={code}
            placeholder="SG-XXXX"
            onChange={(e: Event) => {
              setCode(fieldValue(e).toUpperCase());
              setLooked(false);
              setHit(null);
            }}
          />
        </s-box>
        <AppButton
          variant="secondary"
          loading={busy}
          disabled={busy}
          onClick={() => void lookup()}
        >
          Look up
        </AppButton>
      </s-stack>
      {invalid ? (
        <s-box paddingBlockStart="base">
          <s-paragraph color="subdued">Enter a code like SG-XXXX.</s-paragraph>
        </s-box>
      ) : null}
      {looked && !invalid && hit?.found ? (
        <s-box paddingBlockStart="base" padding="base" borderWidth="base" borderRadius="base">
          <s-stack gap="base">
            <s-paragraph>
              {hit.code}
              {hit.country_code
                ? ` · ${countryLabel(hit.country_code)} visitor`
                : ""}
              {` · ${eventLabel}`}
              {hit.is_test ? " · test preview" : ""}
              {hit.created_at
                ? ` · ${new Date(hit.created_at).toLocaleString()}`
                : ""}
            </s-paragraph>
            {matchedRule ? (
              <s-stack direction="inline" gap="base">
                <AppButton
                  variant="secondary"
                  to={`/app/block/${matchedRule.id}`}
                >
                  Open rule
                </AppButton>
                <AppButton
                  variant="tertiary"
                  to={`/app/block/${matchedRule.id}`}
                >
                  Stop blocking{" "}
                  {hit.country_code
                    ? countryLabel(hit.country_code)
                    : "this country"}
                </AppButton>
              </s-stack>
            ) : null}
          </s-stack>
        </s-box>
      ) : null}
      {looked && !invalid && hit && !hit.found ? (
        <s-box paddingBlockStart="base">
          <s-paragraph color="subdued">
            {hit.message || "No visitor matches that code"}
          </s-paragraph>
        </s-box>
      ) : null}
    </s-section>
  );
}

function BlockCopyEditor({
  copy,
  setCopyDraft,
  previewTitle,
  previewMessage,
}: {
  copy: BlockCopyDraft;
  setCopyDraft: (next: BlockCopyDraft) => void;
  previewTitle: string;
  previewMessage: string;
}) {
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  return (
    <s-grid
      gridTemplateColumns="minmax(0, 1fr) minmax(0, 1fr)"
      gap="base"
    >
      <s-stack gap="base">
        <s-text-field
          label="Title"
          value={copy.title}
          onChange={(e: Event) =>
            setCopyDraft({
              ...copy,
              title: fieldValue(e).slice(0, 120),
            })
          }
          details="Use {country} for the visitor’s country name"
        />
        <s-text-field
          label="Message"
          value={copy.message}
          multiline={3}
          onChange={(e: Event) =>
            setCopyDraft({
              ...copy,
              message: fieldValue(e).slice(0, 280),
            })
          }
        />
        <s-checkbox
          label='Show a “Contact us” link'
          checked={copy.showContact}
          onChange={(e: Event) =>
            setCopyDraft({
              ...copy,
              showContact: fieldChecked(e),
            })
          }
        />
        <s-paragraph color="subdued">
          Contact uses your shop contact email. A reference code (SG-XXXX) is
          added automatically on the block page. Search bots aren’t blocked, so
          Google can still index your store.
        </s-paragraph>
      </s-stack>
      <s-stack gap="base">
        <s-stack
          direction="inline"
          gap="base"
          alignItems="center"
          justifyContent="space-between"
        >
          <s-heading>Live preview</s-heading>
          <s-stack direction="inline" gap="small">
            <s-button
              type="button"
              variant={device === "desktop" ? "primary" : "secondary"}
              onClick={() => setDevice("desktop")}
            >
              Desktop
            </s-button>
            <s-button
              type="button"
              variant={device === "mobile" ? "primary" : "secondary"}
              onClick={() => setDevice("mobile")}
            >
              Mobile
            </s-button>
          </s-stack>
        </s-stack>
        <BlockPreview
          title={previewTitle}
          message={previewMessage}
          showContact={copy.showContact}
          device={device}
        />
      </s-stack>
    </s-grid>
  );
}

function BlockTable({
  rules,
  busy,
  onToggle,
}: {
  rules: SafegeoRule[];
  busy: boolean;
  onToggle: (id: string, enabled: boolean) => void;
}) {
  if (rules.length === 0) {
    return (
      <s-box padding="base" borderWidth="base" borderRadius="base">
        <s-stack direction="block" gap="base">
          <s-text type="strong">No blocked countries yet</s-text>
          <s-paragraph color="subdued">
            Add countries you don’t sell to. They won’t see your storefront.
          </s-paragraph>
          <AppButton variant="primary" to="/app/block/new">
            Block a country
          </AppButton>
        </s-stack>
      </s-box>
    );
  }

  return (
    <s-table>
      <s-table-header-row>
        <s-table-header listSlot="primary">Countries</s-table-header>
        <s-table-header>Status</s-table-header>
        <s-table-header>Actions</s-table-header>
      </s-table-header-row>
      <s-table-body>
        {rules.map((rule) => (
          <s-table-row key={rule.id}>
            <s-table-cell>
              <AppLink to={`/app/block/${rule.id}`}>
                {rule.countries?.map(countryLabel).join(", ") || "-"}
              </AppLink>
            </s-table-cell>
            <s-table-cell>
              <s-badge tone={rule.is_enabled ? "success" : "info"}>
                {rule.is_enabled ? "Active" : "Off"}
              </s-badge>
            </s-table-cell>
            <s-table-cell>
              <s-stack direction="inline" gap="small">
                <AppButton variant="tertiary" to={`/app/block/${rule.id}`}>
                  Edit
                </AppButton>
                <s-button
                  variant="tertiary"
                  disabled={busy}
                  onClick={() => onToggle(rule.id, !rule.is_enabled)}
                >
                  {rule.is_enabled ? "Turn off" : "Turn on"}
                </s-button>
              </s-stack>
            </s-table-cell>
          </s-table-row>
        ))}
      </s-table-body>
    </s-table>
  );
}
